import { DateQuestionModeType, DateValidationType } from './types';

/**
 * Minimum allowed values for offsetHours based on date question mode.
 * Represents the minimum time offset in hours that can be configured
 * for temporal validations (after, before, between, etc.).
 */
export const MIN_OFFSET_ALLOWED_HOURS: Record<DateQuestionModeType, number> = {
  datetime: 24,
  date: 24,
  time: 1,
};

/**
 * Maximum allowed values for offsetHours based on date question mode.
 * Represents the maximum time offset in hours that can be configured
 * for temporal validations.
 * null indicates no maximum limit.
 */
export const MAX_OFFSET_ALLOWED_HOURS: Record<DateQuestionModeType, number | null> = {
  datetime: null,
  date: null,
  time: 23,
};

/**
 * The offset bounds for a question, given its subtype and its validation mode.
 * The single place both modes' bounds are decided — `max: null` means no ceiling.
 */
export const getOffsetBounds = (
  dateMode: DateQuestionModeType | undefined,
  dateValidationType?: DateValidationType | '',
): { min: number; max: number | null } => {
  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';

  if (dateValidationType === 'relativeRange') {
    return {
      // The fixed mode's 24-hour floor does not carry over. There it means
      // "at least a day away from now", which is a sane minimum for a rule
      // anchored to the answering moment. Here the anchor is another answer,
      // and "two hours after the start time" is the most ordinary thing an
      // administrator writes. Keeping the floor would make it unexpressable.
      min: 0,
      // `datetime` splits the offset into a Días field (unbounded) and an Horas field
      // (0-23, enforced by CustomRangeConditionFields itself, not here) that combine into
      // this single stored value — so days absorb anything above a day and the combined
      // total has no ceiling, same as `date`. `time` has no Días field at all: its single
      // Horas field genuinely is the whole offset, so its 23h ceiling is real (PRD; DS-4176
      // review — the comment here used to describe this split without the code matching it).
      max: safeDateMode === 'time' ? 23 : null,
    };
  }

  return {
    min: MIN_OFFSET_ALLOWED_HOURS[safeDateMode],
    max: MAX_OFFSET_ALLOWED_HOURS[safeDateMode],
  };
};
